/**
 * AdminUserDetailPage - User detail view with admin actions
 *
 * Feature: 045-admin-user-tool
 * User Story 1: Search and View User Details
 * User Story 2: Manage User Account Status
 * User Story 3: Edit User Profile Information
 */

import { Link, useParams } from 'react-router-dom';
import { useUserGraphQL } from '../../graphql/hooks/useUserGraphQL';
import { UserDetailView } from '../../components/admin-users/UserDetailView';
import { adminUsersUrl, adminUserRelationshipsUrl } from '../../utils/urls';

export function AdminUserDetailPage() {
  const { globalId } = useParams<{ globalId: string }>();
  const { data: userDetail, isLoading, refetch } = useUserGraphQL(globalId);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-surface-2 rounded w-1/3"></div>
            <div className="h-64 bg-surface-2 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!userDetail) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-surface-1 rounded-lg border border-border p-8 text-center">
            <h2 className="text-xl font-semibold text-foreground mb-2">User Not Found</h2>
            <p className="text-muted mb-4">The requested user could not be found.</p>
            <Link
              to={adminUsersUrl()}
              className="inline-block px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Back to User List
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-foreground">User Details</h1>
          <div className="flex gap-2">
            <Link
              to={adminUserRelationshipsUrl(globalId!)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              View Relationships
            </Link>
            <Link
              to={adminUsersUrl()}
              className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
            >
              Back to List
            </Link>
          </div>
        </div>

        {/* User Detail View */}
        <UserDetailView userDetail={userDetail} onUpdate={() => refetch()} />
      </div>
    </div>
  );
}
